import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AppButton } from "@/components/ui";
import { Layers } from "lucide-react";
import { getScrollBehavior } from "@/hooks/use-scroll-spy";

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const NAV_LINKS = [
  { key: "howItWorks", href: "#how-it-works" },
  { key: "outcomes", href: "#strategic-outcomes" },
  { key: "platform", href: "#platform" },
  { key: "faq", href: "#faq" },
];

/**
 * Mobile slide-out navigation drawer.
 *
 * Slides in from right with backdrop overlay.
 * Close triggers: X button, Escape key, backdrop click, nav link click.
 * Focus trapped inside when open.
 * Rosé Pine Dawn luxury aesthetic.
 */
export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the close button when drawer opens
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }

      // Simple focus trap
      if (e.key === "Tab" && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleNavClick = (href: string) => {
    onClose();
    const id = href.replace("#", "");
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: getScrollBehavior() });
    }
  };

  const scrollToWaitlist = () => {
    onClose();
    const element = document.getElementById("waitlist");
    if (element) {
      element.scrollIntoView({ behavior: getScrollBehavior() });
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal={isOpen}
        aria-label={t("landing.nav.ariaLabel")}
        className={`fixed inset-y-0 right-0 z-50 w-72 bg-background shadow-2xl transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-accent to-success rounded-lg flex items-center justify-center">
              <Layers className="text-background w-4 h-4" />
            </div>
            <span className="text-lg font-semibold text-foreground">
              Neural
            </span>
          </div>
          <AppButton
            ref={closeButtonRef}
            isIconOnly
            variant="ghost"
            onPress={onClose}
            className="rounded-lg p-2 h-auto min-w-0 bg-transparent hover:bg-transparent data-[hovered]:bg-transparent text-foreground-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent transition-colors"
            aria-label={t("landing.nav.close")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </AppButton>
        </div>

        <nav className="flex flex-col p-4" aria-label={t("landing.nav.ariaLabel")}>
          {NAV_LINKS.map((link) => (
            <AppButton
              key={link.key}
              variant="ghost"
              onPress={() => handleNavClick(link.href)}
              className="rounded-xl px-4 py-3 h-auto min-w-0 w-full justify-start bg-transparent hover:bg-surface data-[hovered]:bg-surface text-foreground-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent transition-colors font-semibold text-sm"
            >
              {t(`landing.nav.${link.key}`)}
            </AppButton>
          ))}
          <AppButton
            variant="ghost"
            onPress={() => { onClose(); navigate("/sign-in"); }}
            className="mt-2 rounded-xl px-4 py-3 h-auto min-w-0 w-full justify-start bg-transparent hover:bg-surface data-[hovered]:bg-surface text-foreground-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent transition-colors font-semibold text-sm"
          >
            {t("landing.nav.signIn")}
          </AppButton>
          <AppButton
            onPress={scrollToWaitlist}
            className="mt-4 rounded-full bg-success px-4 py-3 h-auto min-w-0 w-full text-center font-bold text-background hover:shadow-lg transition-all text-sm"
          >
            {t("landing.nav.cta")}
          </AppButton>
        </nav>
      </div>
    </>
  );
}
